import { useTranslation } from 'react-i18next';
import { Box, Grid, Typography } from '@mui/material';
import { Page } from '@/components/Page/Page';
import Table from '@/components/Tables/Table/Table';
import styles from './CERPromotions.module.css';
import { useState } from 'react';
import Button from '@/components/Button/Button';
import { ButtonVariants } from '@/components/Button/Button.types';
import { CellContext, Row } from '@tanstack/react-table';
import { PromotionData } from '@/types/promotions.types';
import {
  filterBooleans,
  filterNumbers,
  filterPromotionOccasions,
} from '@/services/promotions/promotions.helper.service';
import Link from '@/components/Link/Link';
import { DisplayOccasionWithoutIcon } from '@/components/DisplayOccasionWithIcon/DisplayOccasionWithIcon';
import { useCERPromotions, useUpdateCERWindow } from '@/services/promotions/promotions.service';
import { Download, Lock } from 'react-feather';
import { DateTime } from 'luxon';
import Modal from '@/components/Modal/Modal';
import { showToast } from '@/components/Toast/Toast';
import { ToastTypes } from '@/components/Toast/Toast.types';
import axios from 'axios';
import fileDownload from 'js-file-download';
import { useSTKContext } from '@/context/context';

const ViewDetails = ({ row }: CellContext<PromotionData, unknown>) => (
  <Link to={`/promotions/${row.original.promotionId}`}>
    <span className={styles.promotionName}>{row.original.name}</span>
  </Link>
);
const combinable = ({ row }: CellContext<PromotionData, unknown>) => {
  if (row.original.combinable) return 'Yes';
  if (!row.original.combinable) return 'No';
  return '';
};
const isPromoVisible = ({ row }: CellContext<PromotionData, unknown>) => {
  if (row.original.isVisible) return 'Yes';
  if (!row.original.isVisible) return 'No';
  return '';
};

const CERPromotions = () => {
  const { t } = useTranslation();
  const { data: cerPromotions, isLoading, refetch } = useCERPromotions();
  const { mutate: updateCERWindow, isLoading: cerLoading } = useUpdateCERWindow();
  const openedCERWindow = !cerPromotions?.[0]?.isLock;
  const cerButtonText = !openedCERWindow ? t('promotions.startCERWindow') : t('promotions.endCERWindow');
  const { apiClient } = useSTKContext();
  const [registerCount, setRegisterCount] = useState(0);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const closeModal = () => setModalIsOpen(false);
  const openModal = () => setModalIsOpen(true);

  const columns = [
    {
      accessorKey: 'name',
      header: t('promotions.table.name'),
      enableSorting: true,
      cell: ViewDetails,
    },
    {
      accessorKey: 'activeStoreCount',
      header: t('promotions.table.activeStoreCount'),
      enableSorting: true,
      filterFn: (row: Row<PromotionData>, columnId: string, filterValue: any) => {
        const promotion = row.original;
        return filterNumbers(promotion.activeStoreCount, filterValue);
      },
    },
    {
      accessorKey: 'occasions',
      header: t('promotions.table.occasion'),
      enableSorting: true,
      cell: DisplayOccasionWithoutIcon,
      filterFn: (row: Row<PromotionData>, columnId: string, filterValue: any) => {
        const promotion = row.original;
        return filterPromotionOccasions(promotion, filterValue);
      },
    },
    {
      accessorKey: 'combinable',
      header: t('promotions.table.combinable'),
      enableSorting: true,
      cell: combinable,
      filterFn: (row: Row<PromotionData>, columnId: string, filterValue: boolean) => {
        const promotion = row.original;
        return filterBooleans(promotion.combinable, filterValue);
      },
    },
    {
      accessorKey: 'maxUses',
      header: t('promotions.table.maxUses'),
      enableSorting: true,
      filterFn: (row: Row<PromotionData>, columnId: string, filterValue: any) => {
        const promotion = row.original;
        return filterNumbers(promotion.maxUses, filterValue);
      },
    },
    {
      accessorKey: 'isVisible',
      header: t('promotions.table.visible'),
      enableSorting: true,
      cell: isPromoVisible,
      filterFn: (row: Row<PromotionData>, columnId: string, filterValue: boolean) => {
        const promotion = row.original;
        return filterBooleans(promotion.isVisible, filterValue);
      },
    },
  ];

  const currentDate = DateTime.now().toFormat('MM/dd/yyyy');

  const handleConfirm = () => {
    updateCERWindow(openedCERWindow ? 'END' : 'START', {
      onSuccess: () => {
        showToast({
          id: 'updateCERWindow',
          label: openedCERWindow ? 'Success: CER opt-out window ended.' : 'CER Window updated successfully',
        });
        refetch();
        closeModal();
      },
      onError: () => {
        showToast({
          id: 'errorUpdatingCERWindow',
          label: openedCERWindow
            ? 'Failure: Unable to end CER window. Try again.'
            : 'Failure:  Unable to start CER window.  Try again.',
          toastType: ToastTypes.Error,
        });
      },
    });
  };

  const handleDownload = (url: string, filename: string) => {
    apiClient
      .get(url, {
        responseType: 'blob',
      })
      .then((res) => {
        fileDownload(res.data, filename);
      });
  };

  return (
    <Page
      title={t('promotions.title')}
      header={t('promotions.manageCERs')}
      gap={2.0}
      breadcrumb={{ label: t('breadcrumb.backToHome'), to: '/' }}
    >
      <Grid
        container
        direction="row"
        sx={{
          justifyContent: 'space-between',
          alignItems: 'flex-start',
        }}
      >
        <Grid item gap="20px">
          <Grid
            container
            direction="row"
            sx={{
              alignItems: 'flex-end',
            }}
          >
            <Grid item>
              <Button onClick={openModal}>{cerButtonText}</Button>
            </Grid>
            <Grid
              item
              paddingLeft={1.5}
              mb={0.7}
              alignContent="center"
              alignItems="center"
              sx={{ justifyContent: 'center' }}
            >
              <Button
                variant={ButtonVariants.Text}
                onClick={() => {
                  handleDownload('/promotions/cer-opt-in-out-report', `CER_Promotion_Report_${currentDate}.zip`);
                }}
              >
                <Download size={16} />
                {t('promotions.exportCERReport')}
              </Button>
            </Grid>
          </Grid>
        </Grid>
        <Grid item>
          <Typography
            sx={{
              fontSize: '12px',
              color: '#5a5a5a',
            }}
          >
            <Grid container direction="row">
              <Grid item pr={0.5} mt={2.5}>
                <Lock size={16} strokeWidth={2} />
              </Grid>
              <Grid item pr={0.5} mt={2.5}>
                {`${registerCount} ${
                  openedCERWindow
                    ? t('promotions.CERPromotionsUnlockedCount')
                    : t('promotions.CERPromotionsLockedCount')
                }`}
              </Grid>
            </Grid>
          </Typography>
        </Grid>
      </Grid>
      <Box>
        <div className={styles.divider} />
        <Table
          height="calc(100vh - 170px)"
          isLoading={isLoading}
          data={cerPromotions}
          columns={columns}
          setRegisterCount={setRegisterCount}
          enableFilters
        />
      </Box>
      {modalIsOpen && (
        <Modal
          className={styles.deleteModal}
          isOpen={modalIsOpen}
          onClose={closeModal}
          showTitleDivider
          maxWidth="670px"
          headerContent={openedCERWindow ? t('promotions.endCEROptOut') : t('promotions.startCEROptOut')}
          bodyContent={
            <div className={styles.message}>
              {openedCERWindow ? t('promotions.endCERUnlockModalCopy') : t('promotions.startCERUnlockModalCopy')}
            </div>
          }
          footerContent={
            <div className={styles.modalButtons}>
              <Button variant={ButtonVariants.Secondary} onClick={closeModal} disabled={cerLoading} type="button">
                {t('common.cancel')}
              </Button>
              <Button onClick={handleConfirm} type="button" isLoading={cerLoading}>
                {t('common.confirm')}
              </Button>
            </div>
          }
        />
      )}
    </Page>
  );
};

export default CERPromotions;
